(() => {
  'use strict';

  const storageKey = 'nuviaseed_blog_attribution_v1';
  const pendingKey = 'nuviaseed_blog_add_pending_v1';
  const lifetimeMs = 30 * 24 * 60 * 60 * 1000;
  const articlePattern = /^\/blogs\/([^/]+)\/([^/?#]+)\/?$/;
  const commercePattern = /^\/(products|collections)\/([^/?#]+)/;

  function analyticsAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return !privacy?.analyticsProcessingAllowed || privacy.analyticsProcessingAllowed();
    } catch (_error) {
      return false;
    }
  }

  function safeStorage(method, key, value) {
    try {
      if (method === 'get') return window.localStorage.getItem(key);
      if (method === 'set') window.localStorage.setItem(key, value);
      if (method === 'remove') window.localStorage.removeItem(key);
    } catch (_error) {
      return null;
    }
    return null;
  }

  function articleContext() {
    const match = window.location.pathname.match(articlePattern);
    if (!match) return null;
    return {
      blog_handle: decodeURIComponent(match[1]),
      article_handle: decodeURIComponent(match[2]),
      article_url: `${window.location.origin}${window.location.pathname}`,
    };
  }

  function commerceDestination(anchor) {
    let url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (_error) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    const match = url.pathname.match(commercePattern);
    if (!match) return null;
    return {
      destination_type: match[1] === 'products' ? 'product' : 'collection',
      destination_handle: decodeURIComponent(match[2]),
      destination_url: `${url.origin}${url.pathname}`,
    };
  }

  function publish(name, payload) {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (_error) {
      // Shopify's pixel runtime owns consent and delivery for this channel.
    }
    if (!analyticsAllowed()) return;
    const googlePayload = {event_category: 'blog_commerce', ...payload};
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, googlePayload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({event: name, ...googlePayload});
    }
  }

  function remember(attribution) {
    if (!analyticsAllowed()) return;
    safeStorage('set', storageKey, JSON.stringify(attribution));
  }

  function storedAttribution() {
    if (!analyticsAllowed()) return null;
    const raw = safeStorage('get', storageKey);
    if (raw) {
      try {
        const attribution = JSON.parse(raw);
        if (attribution?.article_handle && Date.now() <= Number(attribution.expires_at || 0)) return attribution;
        safeStorage('remove', storageKey);
      } catch (_error) {
        safeStorage('remove', storageKey);
      }
    }
    const articleHandle = new URLSearchParams(window.location.search).get('blog_ref');
    if (!articleHandle || !/^[a-z0-9-]+$/.test(articleHandle)) return null;
    const currentDestination = window.location.pathname.match(commercePattern);
    const now = Date.now();
    const attribution = {
      blog_handle: 'gardening-blog',
      article_handle: articleHandle,
      article_url: `${window.location.origin}/blogs/gardening-blog/${articleHandle}`,
      destination_type: currentDestination?.[1] === 'products' ? 'product' : 'collection',
      destination_handle: currentDestination?.[2] || '',
      destination_url: `${window.location.origin}${window.location.pathname}`,
      link_text: '',
      placement: 'tracked_navigation',
      clicked_at: new Date(now).toISOString(),
      expires_at: now + lifetimeMs,
    };
    remember(attribution);
    return attribution;
  }

  function decorateForNavigation(anchor, attribution) {
    if (!analyticsAllowed()) return;
    try {
      const url = new URL(anchor.href, window.location.origin);
      url.searchParams.set('blog_ref', attribution.article_handle);
      url.searchParams.set('blog_link', attribution.destination_handle);
      anchor.href = url.toString();
    } catch (_error) {
      // The original working destination remains unchanged.
    }
  }

  function handleArticleClick(event) {
    const context = articleContext();
    if (!context) return;
    const anchor = event.target.closest?.('.article-template__content a[href]');
    if (!anchor) return;
    const destination = commerceDestination(anchor);
    if (!destination) return;
    const now = Date.now();
    const attribution = {
      ...context,
      ...destination,
      link_text: (anchor.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120),
      placement: anchor.closest('[data-nuviaseed-commerce-cta]') ? 'shop_the_next_step' : 'article_body',
      clicked_at: new Date(now).toISOString(),
      expires_at: now + lifetimeMs,
    };
    remember(attribution);
    decorateForNavigation(anchor, attribution);
    publish('blog_commerce_link_clicked', attribution);
  }

  function handleAttributedNavigation(event) {
    if (articleContext()) return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const destination = commerceDestination(anchor);
    if (!destination) return;
    const attribution = storedAttribution();
    if (!attribution) return;
    const updated = {...attribution, ...destination};
    remember(updated);
    decorateForNavigation(anchor, updated);
  }

  function addHiddenProperty(form, name, value) {
    let input = [...form.elements].find((field) => field.name === name);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.append(input);
    }
    input.value = value;
  }

  function attributeForm(form, attribution) {
    if (!form?.matches?.('form[action*="/cart/add"]')) return;
    addHiddenProperty(form, 'properties[_Blog article]', attribution.article_handle);
    addHiddenProperty(form, 'properties[_Blog article URL]', attribution.article_url);
    addHiddenProperty(form, 'properties[_Blog link destination]', attribution.destination_url);
    addHiddenProperty(form, 'properties[_Blog click time]', attribution.clicked_at);
  }

  function attributeProductForms(root = document) {
    const attribution = storedAttribution();
    if (!attribution) return;
    root.querySelectorAll?.('form[action*="/cart/add"]').forEach((form) => attributeForm(form, attribution));
  }

  function handleAddAttempt(event) {
    const form = event.target;
    if (!form?.matches?.('form[action*="/cart/add"]')) return;
    const attribution = storedAttribution();
    if (!attribution) return;
    attributeForm(form, attribution);
    const pending = {
      ...attribution,
      product_handle: window.location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || '',
      variant_id: String(new FormData(form).get('id') || ''),
      quantity: Number(new FormData(form).get('quantity') || 1),
      attempted_at: new Date().toISOString(),
    };
    try {
      window.sessionStorage.setItem(pendingKey, JSON.stringify(pending));
    } catch (_error) {
      // The line-item properties still carry server-side attribution.
    }
  }

  function pendingAdd() {
    try {
      const raw = window.sessionStorage.getItem(pendingKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  function clearPendingAdd() {
    try {
      window.sessionStorage.removeItem(pendingKey);
    } catch (_error) {
      // No action is needed when session storage is unavailable.
    }
  }

  function subscribeToSuccessfulAdds() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form') return;
      const attribution = pendingAdd();
      if (!attribution) return;
      publish('blog_attributed_add_to_cart', {
        ...attribution,
        variant_id: String(event.productVariantId || attribution.variant_id),
        added_at: new Date().toISOString(),
      });
      clearPendingAdd();
    });
  }

  document.addEventListener('click', handleArticleClick, true);
  document.addEventListener('click', handleAttributedNavigation, true);
  document.addEventListener('submit', handleAddAttempt, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      attributeProductForms();
      subscribeToSuccessfulAdds();
    }, {once: true});
  } else {
    attributeProductForms();
    subscribeToSuccessfulAdds();
  }

  const observer = new MutationObserver((mutations) => {
    if (!storedAttribution()) return;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('form[action*="/cart/add"]')) attributeForm(node, storedAttribution());
        attributeProductForms(node);
      }
    }
  });
  observer.observe(document.documentElement, {childList: true, subtree: true});
})();
